/**
 * OutreachStatusCard (KAN-643, KAN-660)
 *
 * Dashboard block "Рассылка сегодня": is Telegram outreach running or stuck.
 * Header: one big status with its reason (`headline`), the day timeline with
 * the sending window, accounts limit and sent marks (`OutreachTimeline`), then
 * three lines (KAN-710): sent today; what else goes out before the window end
 * (forecast, per-account breakdown in the tooltip); what of the accounts limit
 * will not go out today and why (`forecast.blocked`, hidden when zero).
 * Body: accounts with their state, queue per campaign, background services.
 * Data: GET /api/v1/dashboard/outreach-status/ (refetched every 30 s).
 */
import { Loader2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  useOutreachStatus,
  type OutreachAccount,
  type OutreachAccountState,
  type OutreachForecast,
  type OutreachForecastBlocked,
  type OutreachHeadline,
  type OutreachHeadlineState,
  type OutreachService,
  type OutreachStatus,
} from '@/services/api/dashboard'
import { OutreachTimeline } from './OutreachTimeline'

const HEADLINE_STYLES: Record<OutreachHeadlineState, string> = {
  running: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  paused: 'border-amber-200 bg-amber-50 text-amber-700',
  outside_window: 'border-amber-200 bg-amber-50 text-amber-700',
  limit: 'border-gray-200 bg-gray-50 text-gray-600',
  idle: 'border-gray-200 bg-gray-50 text-gray-600',
  error: 'border-rose-200 bg-rose-50 text-rose-700',
}

/** Headline for an older backend without `headline`: derived from `status`. */
const LEGACY_HEADLINE: Record<OutreachStatus['status'], OutreachHeadline> = {
  running: { state: 'running', text: 'Идёт', until: null, reason: '' },
  waiting: { state: 'idle', text: 'Ждёт окна/пауз', until: null, reason: '' },
  stopped: { state: 'error', text: 'Встала', until: null, reason: '' },
}

const STATE_LABELS: Record<OutreachAccountState, string> = {
  writing: 'Пишет',
  cooldown: 'Пауза',
  limit: 'Лимит',
  peer_flood: 'PeerFlood',
  dead: 'Нет связи',
  proxy_down: 'Прокси не работает',
  disabled: 'Выключен',
  decommissioned: 'Списан',
  outside_window: 'Вне окна',
}

const STATE_STYLES: Record<OutreachAccountState, string> = {
  writing: 'bg-emerald-100 text-emerald-700',
  cooldown: 'bg-sky-100 text-sky-700',
  limit: 'bg-gray-100 text-gray-600',
  peer_flood: 'bg-amber-100 text-amber-700',
  dead: 'bg-rose-100 text-rose-700',
  proxy_down: 'bg-rose-100 text-rose-700',
  disabled: 'bg-gray-100 text-gray-500',
  decommissioned: 'bg-gray-100 text-gray-400',
  outside_window: 'bg-gray-100 text-gray-600',
}

const SERVICE_LABELS: Record<OutreachService['name'], string> = {
  beat: 'Beat',
  planner: 'Планировщик',
  gateway: 'Держатель',
}

/** HH:MM in Moscow time. */
function mskTime(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Moscow',
  })
}

function stateText(account: OutreachAccount): string {
  const label = STATE_LABELS[account.state]
  if ((account.state === 'cooldown' || account.state === 'peer_flood') && account.until) {
    return `${label} до ${mskTime(account.until)}`
  }
  if (account.state === 'outside_window' && account.next_send_at) {
    return `${label}, с ${mskTime(account.next_send_at)}`
  }
  return label
}

/** KAN-719: proxy_down tooltip: the failed check error and the proxy address. */
function stateTitle(account: OutreachAccount): string | undefined {
  if (account.state !== 'proxy_down') return undefined
  const proxy = account.proxy_problem?.proxy
  const error = account.detail || account.proxy_problem?.error || ''
  return [error, proxy].filter(Boolean).join(', ') || undefined
}

function ageText(service: OutreachService): string {
  if (service.age_s === null) return 'нет данных'
  if (service.age_s < 120) return `${service.age_s} сек назад`
  return `${Math.round(service.age_s / 60)} мин назад`
}

/** KAN-710: line 2, what else goes out before the window closes. */
function forecastText(forecast: OutreachForecast, windowEnd: string | null): string {
  const until = windowEnd ? `до ${windowEnd}` : 'сегодня'
  return `Ещё уйдёт ${until}: ~${forecast.will_send} (в очереди ${forecast.queue_total})`
}

/** KAN-710: one reason of the "will not go out today" line. */
function blockedReasonText({ reason, accounts, slots }: OutreachForecastBlocked): string {
  const count = accounts.length
  switch (reason) {
    case 'peer_flood':
      return `Telegram запретил писать новым (PeerFlood) у ${count} акк.: ${slots}`
    case 'flood_wait':
      return `FloodWait у ${count} акк.: ${slots}`
    case 'proxy_down':
      return `прокси не работает у ${count} акк.: ${slots}`
    case 'disabled':
      return `выключены ${count} акк.: ${slots}`
    case 'cooldown_past_window':
      return `не успеют до конца окна: ${slots}`
    case 'queue_empty':
      return `очередь пуста: ${slots}`
    default:
      return `другое: ${slots}`
  }
}

/** KAN-710: line 3, what of the accounts limit will not go out today; hidden when zero. */
function Blocked({ blocked }: { blocked: OutreachForecastBlocked[] | undefined }) {
  const reasons = (blocked ?? []).filter((item) => item.slots > 0)
  const total = reasons.reduce((sum, item) => sum + item.slots, 0)
  if (total === 0) return null
  return (
    <p data-testid="outreach-blocked" className="text-sm text-amber-700">
      Не уйдёт сегодня: {total} мест (
      {reasons.map((item, index) => (
        <span key={`${item.reason}:${index}`}>
          {index > 0 && '; '}
          <span
            data-testid="outreach-blocked-reason"
            data-reason={item.reason}
            title={item.accounts.join(', ') || undefined}
          >
            {blockedReasonText(item)}
          </span>
        </span>
      ))}
      )
    </p>
  )
}

/** KAN-683: per-account breakdown for the tooltip, one account per line. */
function forecastTitle(forecast: OutreachForecast): string {
  return forecast.per_account
    .map((a) => `${a.label}: лимит ${a.limit_left}, по окну ${a.window_capacity}, будет ${a.will}`)
    .join('\n')
}

function Forecast({ forecast, windowEnd }: { forecast: OutreachForecast; windowEnd: string | null }) {
  return (
    <>
      <p
        data-testid="outreach-forecast"
        data-bound={forecast.bound_by}
        title={forecastTitle(forecast) || undefined}
        className="text-sm text-gray-600"
      >
        {forecastText(forecast, windowEnd)}
      </p>
      <Blocked blocked={forecast.blocked} />
    </>
  )
}

function Headline({ headline }: { headline: OutreachHeadline }) {
  return (
    <div
      data-testid="outreach-status"
      data-state={headline.state}
      className={`mt-2 rounded-md border px-3 py-2 text-lg font-semibold leading-snug ${HEADLINE_STYLES[headline.state]}`}
    >
      {headline.text}
    </div>
  )
}

function AccountRow({ account }: { account: OutreachAccount }) {
  return (
    <li data-testid="outreach-account" className="flex items-center justify-between gap-2 py-1 text-sm">
      <span className="min-w-0 truncate text-gray-900">{account.label}</span>
      <span className="flex shrink-0 items-center gap-2">
        <span className="tabular-nums text-gray-500">
          {account.sent_today}/{account.limit}
        </span>
        <span
          data-testid="outreach-account-state"
          data-state={account.state}
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATE_STYLES[account.state]}`}
          title={stateTitle(account)}
        >
          {stateText(account)}
        </span>
      </span>
    </li>
  )
}

interface Props {
  /** Current moment for the timeline (tests); defaults to the render time. */
  now?: Date
}

export function OutreachStatusCard({ now }: Props = {}) {
  const { data, isLoading, error } = useOutreachStatus()
  const current = now ?? new Date()
  const headline = data ? (data.headline ?? LEGACY_HEADLINE[data.status]) : null

  return (
    <Card data-testid="outreach-status-card">
      <CardHeader className="pb-3">
        <CardTitle>Рассылка сегодня</CardTitle>
        {data && headline && (
          <>
            <Headline headline={headline} />
            <OutreachTimeline
              window={data.window}
              sentTimes={data.sent_times ?? []}
              planned={data.planned ?? []}
              capacity={data.progress.capacity}
              sent={data.progress.sent_today}
              now={current}
            />
            <p data-testid="outreach-progress" className="text-sm text-gray-600">
              Отправлено сегодня: {data.progress.sent_today}
            </p>
            {data.forecast && <Forecast forecast={data.forecast} windowEnd={data.window?.end ?? null} />}
          </>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : error || !data ? (
          <p className="py-6 text-center text-sm text-red-600">Ошибка загрузки данных</p>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <h4 className="mb-1 text-xs font-medium uppercase text-gray-400">Аккаунты</h4>
              {data.accounts.length === 0 ? (
                <p className="text-sm text-gray-500">Нет аккаунтов рассылки</p>
              ) : (
                <ul className="divide-y">
                  {data.accounts.map((account) => (
                    <AccountRow key={`${account.operator_id}:${account.id}`} account={account} />
                  ))}
                </ul>
              )}
            </div>
            <div>
              <h4 className="mb-1 text-xs font-medium uppercase text-gray-400">Очередь</h4>
              {data.queue.length === 0 ? (
                <p className="text-sm text-gray-500">Нет активных кампаний</p>
              ) : (
                <ul className="divide-y">
                  {data.queue.map((campaign) => (
                    <li
                      key={campaign.id}
                      data-testid="outreach-campaign"
                      className="flex items-center justify-between gap-2 py-1 text-sm"
                    >
                      <span className="min-w-0 truncate text-gray-900">{campaign.name}</span>
                      <span className="shrink-0 text-gray-500">
                        новых {campaign.new}, в очереди {campaign.pending + campaign.claimed}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
        {data && (
          <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 border-t pt-3 text-xs">
            {data.services.map((service) => (
              <span
                key={service.name}
                data-testid={`outreach-service-${service.name}`}
                className={service.ok ? 'text-gray-500' : 'font-medium text-rose-600'}
              >
                {SERVICE_LABELS[service.name]}: {ageText(service)}
              </span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
